import { useEffect, useState } from 'react';
import { initialize } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { parse } from 'query-string';

import {
  FormsEnum,
  InsuranceTypeEnum,
  QuotationStatusEnum,
} from 'app/shared/enums';
import { FormConfig } from 'app/shared/interfaces/FormConfig.interface';
import { clearQuotation, getQuotationStatus } from 'app/store/data/quotation';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';
import { CalculationDataByKeysInterface } from '../interfaces/CalculationDataByKeys.interface';
import { useUser } from './useUser';
import { FormsConfigActions } from 'app/store/data/formsConfig';
import { StorageService } from 'app/services/storage.service';
import getInitialValues from 'app/views/CalculationView/getInitialValues';
import removeQueryParams from 'app/shared/helpers/removeQueryParams';
import { useForm } from 'app/shared/hooks/useForm';
import { ShortPathDataActions } from 'app/store/data/shortPathData';

export function useClearCalculationForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isOperator, user } = useUser();
  const quotationStatus = useSelector(getQuotationStatus);
  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);
  const [isScopeOrLeasingWasChange, setIsScopeOrLeasingWasChange] = useState(
    false
  );
  const [initRun, setInitRun] = useState(true);

  const { formValues } = useForm<CalculationDataByKeysInterface>(
    FormsEnum.CalculationForm
  );

  useEffect(() => {
    if (formValues && initRun) {
      setInitRun(false);

      const shouldShowClearFormBox = isFormMicroCenterActive
        ? !!(formValues.car?.productionYear || formValues.insurance?.startDate)
        : !!(
            formValues.car?.registrationNumber ||
            formValues.car?.registrationNumberNotKnown ||
            formValues.owner?.birthDate
          );

      if (shouldShowClearFormBox) {
        setIsScopeOrLeasingWasChange(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, isFormMicroCenterActive]);

  const getHideClearFormBox = () => {
    return Boolean(location.state && location.state.hideClearFormBox);
  };

  const getIsCheckDataParamSet = () => {
    const checkData = parse(location.search).cd;

    return checkData === '1' || checkData === '2';
  };

  const getIsClearFormBoxVisible = () => {
    return (
      (quotationStatus === QuotationStatusEnum.InProgress ||
        quotationStatus === QuotationStatusEnum.Calculated) &&
      !getIsCheckDataParamSet() &&
      user &&
      !isOperator &&
      !getHideClearFormBox() &&
      !sessionStorage.getItem('isActiveSession') &&
      isScopeOrLeasingWasChange
    );
  };

  const removeClearFormAndCheckDataParams = () => {
    const newQueryString = removeQueryParams(['cd', 'clear-form']);

    history.replace(`${location.pathname}${newQueryString}`);
  };

  const clearForm = async () => {
    const initValues = {
      insurance: {
        scope: [InsuranceTypeEnum.Oc],
      },
    } as CalculationDataByKeysInterface;

    sessionStorage.removeItem('diffFirstRegDate');

    dispatch(ShortPathDataActions.clearShortPathData());
    await dispatch(clearQuotation());
    dispatch(
      FormsConfigActions.setOverwrittenFormConfigAction({
        config: {} as FormConfig,
        shouldUpdate: false,
      })
    );

    const values =
      user && !isOperator ? getInitialValues(initValues, user) : {};

    dispatch(initialize(FormsEnum.CalculationForm, values));
    dispatch(
      FormsConfigActions.clearFormErrorsAction({
        formName: FormsEnum.CalculationForm,
      })
    );
    dispatch(
      FormsConfigActions.setActiveScreenAction({
        screen: 0,
        formName: FormsEnum.CalculationForm,
      })
    );

    if (isFormMicroCenterActive) {
      dispatch(FormsConfigActions.setFirstStepMode(true));
    }

    StorageService.clearReachedScreen(FormsEnum.CalculationForm);
    StorageService.clearReachedScreen(FormsEnum.CheckoutForm);
    removeClearFormAndCheckDataParams();
  };

  return {
    clearForm,
    getIsClearFormBoxVisible,
  };
}
